import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import {
  Button,
  EmptyStateProps,
  FilterDropdown,
  Pagination,
  TabItem,
  Tabs,
  failedEmptyState,
  useLoadPhase,
} from "../../../components/ui";
import { userDetailPath } from "../../../routes/paths";
import PaymentsTable from "../components/PaymentsTable";
import useRevenue, { PAYMENTS_PER_PAGE } from "../hooks/useRevenue";
import { PAYMENT_TABS } from "../paymentStyles";
import {
  DATE_RANGE_OPTIONS,
  DEFAULT_DATE_RANGE,
  NO_DATE_RANGE,
  type DateRangeId,
} from "../dateRanges";
import type { PaymentTabId } from "../types/revenue.types";

/** What each tab says when its list comes back empty. The reason is different
 *  every time, and "no results" for all five would answer none of them. */
const EMPTY_COPY: Record<PaymentTabId, { title: string; description: string }> =
  {
    all: {
      title: "No payments in this period",
      description:
        "Nothing has been billed in the range you are looking at. Try a wider one.",
    },
    active: {
      title: "No active payments",
      description:
        "Nothing billed in this period is still inside the period it paid for.",
    },
    abandoned: {
      title: "No abandoned checkouts",
      description:
        "Everyone who started a checkout in this period finished it.",
    },
    expired: {
      title: "No expired payments",
      description:
        "No mandate ran out before it could be charged in this period.",
    },
    pending_authorization: {
      title: "Nothing awaiting authorization",
      description:
        "No payment from this period is still waiting on a bank to answer.",
    },
  };

/**
 * What Naurist has been paid, and what it has failed to collect.
 *
 * One card: a range to look at, five tabs across the states a payment can be
 * in, and the list itself. The tabs and the range divide the work between them
 * — the tabs are what happened to the money, the range is when — and both are
 * applied on the server, so the browser never holds the ledger.
 *
 * Nothing here acts on a payment. A row opens the account behind it, and
 * anything that changes one lives there. There is no refunding, no retrying and
 * no cancelling from this screen, because none of those are a console's to do
 * quietly — they are the payment provider's, and they leave a trail there.
 */
const RevenuePage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<PaymentTabId>("all");
  const [range, setRange] = useState<DateRangeId>(DEFAULT_DATE_RANGE);
  const [page, setPage] = useState(1);

  const { payments, status, totalPages, reload } = useRevenue({
    page,
    tab: activeTab,
    range,
  });

  // A list that lands quickly goes straight from blank to filled: the table's
  // placeholder is only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(status === "loading");

  const handleTabChange = (tab: PaymentTabId) => {
    setActiveTab(tab);
    // A different slice has a different number of pages, and page 4 of it may
    // not exist.
    setPage(1);
  };

  const handleRangeChange = (next: DateRangeId) => {
    setRange(next);
    setPage(1);
  };

  // No badges, per the design. The counts arrive with every page under
  // `tabCounts` and are one prop from being shown here, if they are ever
  // wanted — `Tabs` draws a badge for any tab given a `count`.
  const tabs: TabItem<PaymentTabId>[] = PAYMENT_TABS.map((tab) => ({
    id: tab.id,
    label: tab.label,
  }));

  /** What the table shows in place of rows: a fetch that failed, or a slice
   *  of time with nothing in it. Nothing about loading — while the list is out
   *  the table shimmers, and the status line below says so in words. */
  const emptyState = (): EmptyStateProps =>
    status === "failed"
      ? failedEmptyState(
          "We could not load the payments",
          <Button size="sm" variant="secondary" onClick={reload}>
            Try again
          </Button>
        )
      : EMPTY_COPY[activeTab];

  return (
    // The page owns the viewport height instead of growing past it, so the
    // only thing that scrolls is the table body inside the card.
    <div className="h-full flex flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      <PageHeader title="Revenue" />

      <section className="mt-5 flex-1 min-h-0 flex flex-col overflow-hidden rounded-md border border-line bg-white">
        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6 sm:pb-0">
          <h2 className="text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]">
            Payment details
          </h2>

          <FilterDropdown<DateRangeId>
            placeholder="Date range"
            options={DATE_RANGE_OPTIONS}
            value={range}
            onChange={handleRangeChange}
            // "All time" is the option that means no window, so it is the one
            // the control treats as nothing-applied — picking it puts the box
            // back to reading "Date range".
            defaultValue={NO_DATE_RANGE}
            className="w-full sm:w-[180px]"
          />
        </div>

        {/* Inside the card and under its heading, rather than above it the way
            the roster's are: this card is the whole screen, so its tabs are
            part of it rather than a bar the page carries. */}
        <div className="flex-shrink-0 px-4 sm:px-6 mt-4 border-b border-line overflow-x-auto custom-scrollbar-overlay">
          <Tabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
        </div>

        {/* Said as soon as the request goes out, whether or not anything is
            drawn for it: this line is not seen, so it costs nothing to be
            early, and the placeholder it stands for is hidden from screen
            readers — it is the shape of an answer rather than the answer. */}
        {status === "loading" && (
          <p role="status" className="sr-only">
            Loading payments…
          </p>
        )}

        <PaymentsTable
          payments={payments}
          startIndex={(page - 1) * PAYMENTS_PER_PAGE + 1}
          empty={emptyState()}
          phase={phase}
          onSelect={(payment) => navigate(userDetailPath(payment.workspaceId))}
        />

        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          // The rows on screen, not the range's total — the count beside the
          // pager describes the page it is paging.
          resultCount={status === "ready" ? payments.length : undefined}
        />
      </section>
    </div>
  );
};

export default RevenuePage;
